import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { createMyApiKey, listMyApiKeys, revokeMyApiKey, rotateMyApiKey } from "@/lib/partner-portal.functions";
import { PARTNER_SCOPES } from "@/lib/partner-scopes";
import { SiteHeader } from "@/components/site/SiteHeader";
import { PartnerNav } from "@/components/partner/PartnerNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/partner/api-keys")({
  head: () => ({
    meta: [
      { title: "API Keys — Portal de socios" },
      { name: "description", content: "Crea, rota y revoca tus claves de API para la integración con Mostrando México." },
      { property: "og:title", content: "API Keys — Portal de socios" },
      { property: "og:description", content: "Autogestión de claves de API para socios de distribución." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ApiKeysPage,
});

function SecretBanner({ secret, onClose }: { secret: string; onClose: () => void }) {
  return (
    <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
      <div className="text-sm font-semibold">Guarda esta clave ahora — no volverá a mostrarse</div>
      <code className="mt-2 block break-all rounded-lg bg-background p-3 font-mono text-xs">{secret}</code>
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(secret);
            toast.success("Clave copiada");
          }}
        >
          Copiar
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Ya la guardé
        </Button>
      </div>
    </div>
  );
}

function ApiKeysPage() {
  const qc = useQueryClient();
  const { data: keys, isLoading } = useQuery({ queryKey: ["partner", "keys"], queryFn: () => listMyApiKeys() });
  const [secret, setSecret] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState<"sandbox" | "production">("sandbox");
  const [scopes, setScopes] = useState<string[]>(["read:tours", "read:availability"]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["partner"] });
  };

  const create = useMutation({
    mutationFn: () => createMyApiKey({ data: { name, environment, scopes } }),
    onSuccess: (res) => {
      setSecret(res.secret);
      setName("");
      invalidate();
      toast.success("Clave creada");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => rotateMyApiKey({ data: { id, grace_hours: 24 } }),
    onSuccess: (res) => {
      setSecret(res.secret);
      invalidate();
      toast.success("Clave rotada. La anterior expira en 24 h.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeMyApiKey({ data: { id } }),
    onSuccess: () => {
      invalidate();
      toast.success("Clave revocada");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-semibold">API Keys</h1>
        <PartnerNav />

        {secret && <SecretBanner secret={secret} onClose={() => setSecret(null)} />}

        <form
          className="space-y-4 rounded-xl border bg-card p-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return toast.error("Ponle un nombre a la clave");
            if (scopes.length === 0) return toast.error("Selecciona al menos un permiso");
            create.mutate();
          }}
        >
          <h2 className="text-lg font-semibold">Nueva clave</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="key-name">Nombre</Label>
              <Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Integración producción" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="key-env">Entorno</Label>
              <select
                id="key-env"
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as "sandbox" | "production")}
              >
                <option value="sandbox">sandbox</option>
                <option value="production">production</option>
              </select>
            </div>
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Permisos</legend>
            <div className="flex flex-wrap gap-3">
              {PARTNER_SCOPES.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={scopes.includes(s)}
                    onCheckedChange={(v) => setScopes((prev) => (v ? [...prev, s] : prev.filter((p) => p !== s)))}
                  />
                  <span className="font-mono text-xs">{s}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creando…" : "Crear clave"}
          </Button>
        </form>

        <div className="rounded-xl border bg-card p-5">
          <h2 className="text-lg font-semibold">Tus claves</h2>
          {isLoading ? (
            <Skeleton className="mt-3 h-24 w-full" />
          ) : (keys ?? []).length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Todavía no tienes claves.</p>
          ) : (
            <ul className="mt-3 space-y-3">
              {(keys ?? []).map((k: any) => (
                <li key={k.id} className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
                  <div className="min-w-0">
                    <div className="font-medium">{k.name}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {k.key_prefix}…{k.last4} · {k.environment} · {k.status}
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-muted-foreground">{(k.scopes ?? []).join(" ")}</div>
                    {k.expires_at && (
                      <div className="text-[11px] text-muted-foreground">
                        expira {new Date(k.expires_at).toLocaleString("es-MX")}
                      </div>
                    )}
                  </div>
                  {k.status === "active" && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" disabled={rotate.isPending} onClick={() => rotate.mutate(k.id)}>
                        Rotar
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (confirm("¿Revocar esta clave? Dejará de funcionar de inmediato.")) revoke.mutate(k.id);
                        }}
                      >
                        Revocar
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
